import { useEffect, useRef, useState } from 'react'
import {
  useCurrentService,
  useHouseholdSearch,
  useStationCheckin,
  type PendingLabel,
  type SearchHousehold,
  type SearchPerson,
} from './useCheckin.ts'

/**
 * The kiosk, as a state machine: find a family → choose who is coming in →
 * labels → back to the start on its own.
 *
 * Everything a kiosk screen does lives here so the web kiosk and a future
 * mobile one are markup over the same flow. Two timers matter on a foyer
 * device nobody is minding:
 *
 *  - **idle**: a family that walks away mid-way leaves their children's names
 *    on the screen for the next family to see. After `idleSeconds` without a
 *    touch the kiosk returns to the start.
 *  - **done**: after the labels print, the screen counts down and resets, so
 *    the next family does not have to find a "start again" button.
 */

export type KioskPhase = 'search' | 'family' | 'done'

export interface KioskSelection {
  person: SearchPerson
  location: { id: string; name: string }
}

/** Where someone is going: a room, and whether they are serving in it. */
export interface KioskChoice {
  id: string
  name: string
  serving?: boolean
}

export function useKioskFlow(
  stationId: string,
  options: { idleSeconds?: number; doneSeconds?: number } = {},
) {
  const idleSeconds = options.idleSeconds ?? 60
  const doneSeconds = options.doneSeconds ?? 15

  const [phase, setPhase] = useState<KioskPhase>('search')
  const [query, setQuery] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [household, setHousehold] = useState<SearchHousehold | null>(null)
  const [selected, setSelected] = useState<Record<string, KioskChoice>>({})
  const [labels, setLabels] = useState<PendingLabel[]>([])
  const [countdown, setCountdown] = useState(doneSeconds)
  const lastTouch = useRef(Date.now())

  // The service running now (or the one a volunteer picked): only its rooms
  // are offered, and the check-ins are recorded against it.
  const current = useCurrentService()
  const timeId = current.service?.id ?? null
  const search = useHouseholdSearch(submitted, submitted !== '' && phase === 'search', timeId)
  const station = useStationCheckin(stationId)

  const reset = () => {
    setPhase('search')
    setQuery('')
    setSubmitted('')
    setHousehold(null)
    setSelected({})
    setLabels([])
    setCountdown(doneSeconds)
    station.clearPending()
  }

  const touch = () => {
    lastTouch.current = Date.now()
  }

  // A single result goes straight to the family; several wait for a choice.
  // Nothing is shown while a fresh answer is on its way: the cached one may
  // say a child is free who was checked in a minute ago. (Offline, the query
  // is paused rather than fetching, so the cached answer is used.)
  const results = search.isFetching ? [] : (search.data ?? [])
  useEffect(() => {
    if (phase === 'search' && submitted !== '' && results.length === 1 && results[0]) {
      chooseHousehold(results[0])
    }
    // Only when a new result set lands.
  }, [search.data, search.isFetching])

  function chooseHousehold(next: SearchHousehold) {
    touch()
    setHousehold(next)
    // Preselect every child who fits a room and is not already in one — the
    // common case is "all of them, to their usual rooms".
    const preset: Record<string, KioskChoice> = {}
    for (const person of next.members) {
      const room = person.eligibleLocations.find((location) => !location.isFull)
      if (person.isChild && !person.openCheckin && room) {
        preset[person.personId] = { id: room.id, name: room.name }
      }
    }
    setSelected(preset)
    setPhase('family')
  }

  const toggle = (
    person: SearchPerson,
    location?: { id: string; name: string },
    serving = false,
  ) => {
    touch()
    setSelected((current) => {
      const next = { ...current }
      const room = location ?? person.eligibleLocations.find((entry) => !entry.isFull)
      if (!room) return current
      const chosen = next[person.personId]
      if (chosen?.id === room.id && (chosen.serving ?? false) === serving)
        delete next[person.personId]
      else next[person.personId] = { id: room.id, name: room.name, serving }
      return next
    })
  }

  const submit = async () => {
    if (!household) return
    touch()
    const made: PendingLabel[] = []
    for (const person of household.members) {
      const choice = selected[person.personId]
      if (!choice) continue
      made.push(
        await station.checkIn.mutateAsync({
          person,
          location: { id: choice.id, name: choice.name },
          timeId,
          serving: choice.serving === true,
        }),
      )
    }
    setLabels(made)
    setCountdown(doneSeconds)
    setPhase('done')
  }

  // Idle reset on the family screen; countdown reset on the done screen.
  useEffect(() => {
    const timer = setInterval(() => {
      if (phase === 'family' && Date.now() - lastTouch.current > idleSeconds * 1000) reset()
      if (phase === 'done') {
        setCountdown((value) => {
          if (value <= 1) {
            reset()
            return doneSeconds
          }
          return value - 1
        })
      }
    }, 1000)
    return () => clearInterval(timer)
  })

  // Labels for the done screen come from the station hook, which knows when
  // each one has reached the server.
  const shownLabels = labels.map(
    (label) => station.pending.find((entry) => entry.clientId === label.clientId) ?? label,
  )

  return {
    phase,
    query,
    setQuery: (value: string) => {
      touch()
      setQuery(value)
    },
    find: () => {
      touch()
      setSubmitted(query.trim())
    },
    searching: search.isFetching,
    searchError: search.error,
    results,
    noMatch: submitted !== '' && !search.isFetching && search.data?.length === 0,
    household,
    chooseHousehold,
    selected,
    toggle,
    selectedCount: Object.keys(selected).length,
    submit,
    submitting: station.checkIn.isPending,
    submitError: station.checkIn.error,
    labels: shownLabels,
    countdown,
    queued: station.queued,
    /** The service being checked in to; null ⇒ none set, every room offered. */
    service: current.service,
    services: current.times,
    chosenServiceId: current.chosenId,
    chooseService: current.choose,
    refused: station.refused,
    dismissRefused: station.dismiss,
    reset,
    touch,
  }
}
